import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Printer, Trash2 } from 'lucide-react';

import { toast } from 'react-toastify';

import { Button, Modal } from '../../components/ui';
import type { Order, OrderItem } from '../../types/@types/order';
import { formatCurrency } from '../../utils/format-currency';
import { usePayOrder, useTakeawayOrders } from '../../hooks/orders';
import { OrderTicketModal } from '../../components/Receipt/OrderTicketModal';
import type { OrderTicketData } from '../../components/Receipt/OrderTicket';
import { TakeawayChargeModal } from '../../components/takeaway/TakeawayChargeModal';
import { useCancelOrder } from '../orders/hooks/useCancelOrder';
import { ConfirmCancelOrderModal } from '../orders/components/ConfirmCancelOrderModal';
import { useOrderStatusMutation } from '../../hooks/useOrders';

const ORDER_STATUS_LABELS: Record<string, string> = {
  WAITING: 'En espera',
  IN_PRODUCTION: 'En producción',
  READY: 'Listo',
  DONE: 'Listo',
  PAID: 'Pagado',
  PARTIAL: 'Pago parcial',
  CANCELLED: 'Cancelado',
  DELIVERED: 'Entregado',
};

interface TakeawayOrderDetailProps {
  order: Order | null;
  open: boolean;
  onClose: () => void;
  onCancel?: () => void;
}

type OrderWithTotals = Order & {
  totals?: {
    subtotal?: number;
    total?: number;
  };
};

type OrderItemWithCalc = OrderItem & {
  linePriceCalc?: { final?: number };
  line_subtotal?: number;
};

function getItemQuantity(order: Order | null): number {
  if (!order?.items) return 0;
  return order.items.reduce(
    (total, item) => total + (Number.isFinite(item.quantity) ? item.quantity : 0),
    0,
  );
}

function getItemDisplayName(item: OrderItem): string {
  const baseName =
    item.name ??
    (typeof item.product === 'string'
      ? item.product
      : item.product?.name ?? 'Producto');
  if (!Number.isFinite(item.quantity) || item.quantity <= 1) {
    return baseName;
  }
  return `${item.quantity} × ${baseName}`;
}

function getItemTotal(item: OrderItem): number {
  const withCalc = item as OrderItemWithCalc;
  const calcFinal = withCalc.linePriceCalc?.final;
  if (typeof calcFinal === 'number' && Number.isFinite(calcFinal)) {
    return calcFinal;
  }

  const line_subtotal = withCalc.line_subtotal;
  if (typeof line_subtotal === 'number' && Number.isFinite(line_subtotal)) {
    return line_subtotal;
  }

  const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
  const base_price =
    typeof withCalc.base_price === 'number' && Number.isFinite(withCalc.base_price)
      ? withCalc.base_price
      : undefined;
  const productPrice =
    typeof item.product !== 'string' &&
      typeof item.product?.price === 'number' &&
      Number.isFinite(item.product.price)
      ? item.product.price
      : 0;

  const unitPrice = base_price ?? productPrice;
  return unitPrice * quantity;
}

function getOrderTotal(order: Order | null): number {
  if (!order) return 0;
  const totals = (order as OrderWithTotals).totals;
  const totalsTotal = totals?.total;
  if (typeof totalsTotal === 'number' && Number.isFinite(totalsTotal)) {
    return totalsTotal;
  }

  if (typeof order.total === 'number' && Number.isFinite(order.total)) {
    return order.total;
  }

  if (!Array.isArray(order.items)) return 0;

  return order.items.reduce((sum, item) => sum + getItemTotal(item), 0);
}

function getCustomerName(order: Order): string {
  return order.customerName?.trim().length
    ? order.customerName.trim()
    : 'Cliente sin nombre';
}

function TakeawayOrderDetail({ order, open, onClose, onCancel }: TakeawayOrderDetailProps) {
  const total = useMemo(() => getOrderTotal(order), [order]);
  const items = order?.items ?? [];
  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + getItemTotal(item), 0),
    [items],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      titleId="takeaway-order-detail"
      className="max-w-xl"
    >
      <div className="flex items-start justify-between border-b border-border px-6 py-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold uppercase tracking-wide text-text-muted">
            Pedido para llevar
          </p>
          <h2 className="truncate text-lg font-semibold text-text">
            {order ? getCustomerName(order) : 'Pedido'}
          </h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Cerrar detalle"
          onClick={onClose}
          className="h-9 w-9 rounded-full"
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
        {items.length === 0 ? (
          <p className="text-sm text-text-muted">Este pedido no tiene productos.</p>
        ) : (
          <ul className="space-y-4">
            {items.map((item) => {
              const additions = Object.entries(item.additions ?? {});
              const exceptions = item.exceptions ?? [];
              const hasAdditions = additions.length > 0;
              const hasExceptions = exceptions.length > 0;
              return (
                <li key={item.id} className="rounded-xl border border-border px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-text">
                        {getItemDisplayName(item)}
                      </p>
                      {item.notes ? (
                        <p className="mt-1 text-xs text-text-muted">Nota: {item.notes}</p>
                      ) : null}
                      {hasAdditions ? (
                        <p className="mt-1 text-xs text-text-muted">
                          Adiciones:{' '}
                          {additions
                            .map(([name, qty]) =>
                              typeof qty === 'number' && qty > 1
                                ? `${qty} × ${name}`
                                : name,
                            )
                            .join(', ')}
                        </p>
                      ) : null}
                      {hasExceptions ? (
                        <p className="mt-1 text-xs text-text-muted">
                          Sin: {exceptions.join(', ')}
                        </p>
                      ) : null}
                    </div>
                    <p className="shrink-0 text-sm font-semibold text-text">
                      {formatCurrency(getItemTotal(item))}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="space-y-2 border-t border-border px-6 py-4">
        {order?.note ? (
          <p className="text-xs text-text-muted">Nota del pedido: {order.note}</p>
        ) : null}
        <div className="flex items-center justify-between text-sm text-text-muted">
          <span>Subtotal</span>
          <span className="font-semibold text-text">{formatCurrency(subtotal)}</span>
        </div>
        <div className="flex items-center justify-between text-base font-semibold text-text mb-4">
          <span>Total</span>
          <span>{formatCurrency(total)}</span>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          {onCancel && order && (
            <Button
              variant="destructive"
              size="sm"
              onClick={onCancel}
            >
              Cancelar Pedido
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function TakeawayBoard() {
  const navigate = useNavigate();
  const { data: orders, isLoading, isError, error, refetch } = useTakeawayOrders();
  const payOrder = usePayOrder();
  const cancelOrder = useCancelOrder();
  const updateStatus = useOrderStatusMutation();
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);
  const [ticketToPrint, setTicketToPrint] = useState<OrderTicketData | null>(null);
  const [chargingOrder, setChargingOrder] = useState<Order | null>(null);
  const [cancellingOrder, setCancellingOrder] = useState<Order | null>(null);

  const ordersList = orders ?? [];
  const pendingOrderId = payOrder.variables;
  const errorMessage = error instanceof Error ? error.message : undefined;

  const renderOrderCard = (order: Order) => {
    const statusLabel = ORDER_STATUS_LABELS[order.status] ?? order.status;
    const total = getOrderTotal(order);
    const itemsPreview = order.items?.slice(0, 3) ?? [];
    const remainingItems = Math.max((order.items?.length ?? 0) - itemsPreview.length, 0);
    const itemCount = getItemQuantity(order);

    return (
      <div role="listitem" className="h-full" key={order.id}>
        <article className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-border bg-surface p-4 shadow-card transition-shadow hover:shadow-card/80 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/70 focus-within:ring-offset-2 focus-within:ring-offset-surface">
          <div className="space-y-3">
            <header className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-[clamp(18px,2.4vw,22px)] font-semibold text-text">
                  {getCustomerName(order)}
                </p>
                <p className="text-xs text-text-muted">
                  {itemCount} artículo{itemCount === 1 ? '' : 's'}
                </p>
              </div>
              <span className="inline-flex min-h-[28px] items-center rounded-full bg-muted px-3 text-xs font-semibold uppercase tracking-wide text-text">
                {statusLabel}
              </span>
            </header>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-text">
                Total: {formatCurrency(total)}
              </p>
              <ul className="space-y-1 text-xs text-text-muted">
                {itemsPreview.map((item) => (
                  <li key={item.id}>{getItemDisplayName(item)}</li>
                ))}
                {remainingItems > 0 ? (
                  <li>+ {remainingItems} ítem{remainingItems === 1 ? '' : 's'} más</li>
                ) : null}
              </ul>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDetailOrder(order)}
            >
              Ver
            </Button>
            {order.paid ? (
              <Button
                type="button"
                className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white"
                onClick={() => {
                  updateStatus.mutate(
                    { id: order.id, status: 'DELIVERED' },
                    {
                      onSuccess: () => {
                        toast.success('Pedido entregado');
                        refetch();
                      },
                    }
                  );
                }}
                disabled={updateStatus.isPending}
              >
                Entregar
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => setChargingOrder(order)}
              >
                Cobrar
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              className="gap-1.5"
              onClick={() => {
                const items = (order.items || []).map(i => ({
                  qty: Number(i.quantity) || 1,
                  name: i.name || (typeof i.product === 'object' ? i.product?.name : '') || 'Producto',
                  notes: i.notes || undefined,
                  extras: [
                    ...((i.flavors || []).map((f: any) =>
                      `${(f.quantity ?? f.qty ?? 1) > 1 ? ((f.quantity ?? f.qty) + 'x ') : ''}${f.flavor?.name ?? f.name ?? ''}`.trim()).filter(Boolean)),
                    ...(Array.isArray(i.additions) ? i.additions : (i.additions && typeof i.additions === 'object' ? Object.keys(i.additions).map(k => `${k}`) : [])),
                    ...(Array.isArray(i.exceptions) ? i.exceptions.map((e: any) => {
                      const val = String(e);
                      return val.toLowerCase().startsWith('sin ') ? val : `Sin ${val}`;
                    }) : []),
                  ],
                }));
                setTicketToPrint({
                  orderId: String(order.id),
                  title: `PARA LLEVAR: ${getCustomerName(order)}`,
                  orderNumber: order.orderSequence ?? order.id,
                  time: order.createdAt || new Date().toISOString(),
                  items,
                });
              }}
              title="Imprimir comanda"
            >
              <Printer className="h-4 w-4" />
              <span>Comanda</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="gap-1.5 text-danger hover:bg-danger/10 border-danger/30"
              onClick={() => setCancellingOrder(order)}
              title="Cancelar pedido"
            >
              <Trash2 className="h-4 w-4" />
              <span>Cancelar</span>
            </Button>
          </div>
        </article>
      </div>
    );
  };

  const stateCard = () => {
    if (isLoading) {
      return (
        <div role="listitem" className="h-full" key="takeaway-loading">
          <article className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-border bg-surface p-4 shadow-card">
            <div className="space-y-3">
              <div className="space-y-2">
                <div className="h-5 w-3/4 animate-pulse rounded bg-muted" />
                <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
              </div>
              <div className="space-y-2">
                <div className="h-3 w-full animate-pulse rounded bg-muted" />
                <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
              </div>
            </div>
            <div className="h-10 w-full animate-pulse rounded-full bg-muted" />
          </article>
        </div>
      );
    }

    if (isError) {
      return (
        <div role="listitem" className="h-full" key="takeaway-error">
          <article className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-amber-300 bg-amber-50/80 p-4 text-amber-900 shadow-card">
            <div className="space-y-2">
              <p className="text-sm font-semibold">No se pudieron cargar los pedidos.</p>
              {errorMessage ? <p className="text-xs">{errorMessage}</p> : null}
            </div>
            <Button type="button" variant="outline" className="w-full" onClick={() => refetch()}>
              Reintentar
            </Button>
          </article>
        </div>
      );
    }

    if (ordersList.length === 0) {
      return (
        <div role="listitem" className="h-full" key="takeaway-empty">
          <article className="flex h-full flex-col justify-center gap-3 rounded-2xl border border-border bg-surface p-4 text-center shadow-card">
            <p className="text-sm font-semibold text-text">Sin pedidos para llevar</p>
            <p className="text-xs text-text-muted">
              Los pedidos creados aparecerán aquí automáticamente.
            </p>
          </article>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div role="listitem" className="h-full" key="takeaway-create">
        <article className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-border bg-surface p-4 shadow-card transition-shadow hover:shadow-card/80 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/70 focus-within:ring-offset-2 focus-within:ring-offset-surface">
          <div className="space-y-3">
            <header className="space-y-1">
              <p className="text-[clamp(18px,2.4vw,22px)] font-semibold text-text">
                Nuevo pedido para llevar
              </p>
              <p className="text-sm text-text-muted">
                Crea un pedido sin mesa y envíalo directo a cocina.
              </p>
            </header>
          </div>
          <Button
            type="button"
            className="min-h-[44px] w-full rounded-full text-[length:clamp(15px,2.4vw,17px)]"
            onClick={() => navigate('/pos/build/takeaway')}
          >
            Nuevo pedido para llevar
          </Button>
        </article>
      </div>
      {ordersList.map((order) => renderOrderCard(order))}
      {stateCard()}
      <TakeawayOrderDetail
        open={detailOrder !== null}
        order={detailOrder}
        onClose={() => setDetailOrder(null)}
        onCancel={() => {
          if (detailOrder) {
            setCancellingOrder(detailOrder);
            setDetailOrder(null);
          }
        }}
      />
      <OrderTicketModal
        data={ticketToPrint}
        onClose={() => setTicketToPrint(null)}
      />
      {chargingOrder ? (
        <TakeawayChargeModal
          orderId={chargingOrder.id}
          open={!!chargingOrder}
          onClose={() => setChargingOrder(null)}
          onSuccess={() => {
            if (detailOrder?.id === chargingOrder.id) {
              setDetailOrder(null);
            }
            setChargingOrder(null);
            refetch();
          }}
          initialAmount={getOrderTotal(chargingOrder)}
        />
      ) : null}
      {cancellingOrder ? (
        <ConfirmCancelOrderModal
          open={!!cancellingOrder}
          order={cancellingOrder}
          loading={cancelOrder.isPending}
          onClose={() => setCancellingOrder(null)}
          onConfirm={(reason) => {
            cancelOrder.mutate(
              {
                orderId: cancellingOrder.id,
                tableId: typeof cancellingOrder.table === 'object' && cancellingOrder.table !== null ? (cancellingOrder.table as any).number : undefined,
              },
              {
                onSuccess: () => {
                  setCancellingOrder(null);
                  refetch();
                },
              }
            );
          }}
        />
      ) : null}
    </>
  );
}

export default TakeawayBoard;
